// K2D "Riportok" főkönyvi kivonat + karton (w_popup_report_brief). Két külön menüpont / képernyő,
// ugyanaz a szűrő-modell; a mód (Brief = kivonat, Carton = karton) dönti el a Crystal riportot + a
// karton-specifikus mezőket. PDF-only: nincs képernyős eredmény-grid. A mezők a valódi K2D képernyők
// (Főkönyvi kivonat / Főkönyvi karton) alapján.

// A backend enum értékei (int). A frontend a route-ból származtatja (kivonat = 0, karton = 1).
export enum StatementReportType {
  Brief = 0,
  Carton = 1,
}

export interface GeneralLedgerStatementFilter {
  reportType: StatementReportType;

  yearId: number;
  fromDate: string | null;
  toDate: string | null;
  fromAccno: string;
  toAccno: string;
  listLevel: number;

  // Nyitó / záró.
  isOpenOnly: boolean;
  isOpenTotal: boolean;
  fromFirst: boolean;
  isOpen: boolean;
  isClosed: boolean;
  isCloseOnly: boolean;
  isPrevClose: boolean;
  isForeignCurrency: boolean;

  // Tétel-típus.
  isManual: boolean;
  isMachine: boolean;
  isStorno: boolean;
  isAcross: boolean;

  // Megjelenítés / csoportosítás.
  isContraAcc: boolean;
  isPartner0: boolean;
  noPartner: boolean;
  isPartner: boolean;
  isCar: boolean;
  partnerCarNewPage: boolean; // Lapdobás a partner/gépjármű csoport után (K2D is_partner_car_new_page)
  ejectAfter: boolean;
  isLevelStyleDiff: boolean;
  invoiceGroup: boolean;
  isEnlNote: boolean;
  isMix: boolean;

  // Dimenzió szűrés I/II/III.
  isDimcode1: boolean;
  fromDimcode1: string;
  toDimcode1: string;
  isDimcode2: boolean;
  fromDimcode2: string;
  toDimcode2: string;
  isDimcode3: boolean;
  fromDimcode3: string;
  toDimcode3: string;
  // A Dim 1-3 fül három dim-szintje közti kapcsolat: true = VAGY, false = ÉS (ps_dim_or).
  isDimOr: boolean;

  // ===== Karton-specifikus (csak Carton módban) =====
  getGeneralLedger: boolean; // Összesítések: Főkönyvi szám
  getAccumulator: boolean; // Összesítések: Gyűjtő
  getAccountClass: boolean; // Összesítések: Számlaosztály
  showTransId: boolean; // Tr.azon. látható
  ejectAfterAccno: boolean; // Lapdobás fksz. után
  connectedAccno: boolean; // Kapcs. fksz.
  isGroupDim1: boolean; // Csoportosítás dimenzió szerint I/II/III
  isGroupDim2: boolean;
  isGroupDim3: boolean;
  sortBy: string; // Rendezettség
  payMode: number; // Fizetés módja (K2D csgenpaymode.szam; 1 = "-" placeholder = mind)
  reconInclude: string; // Össz. pont. is

  // ===== Speciális dimenzió szűrés (K2D SAK pb_filter_sak / w_specdim, Tól/Ig) =====
  // A hlboktmpext.spec_dimN_from/to oszlopokba megy; a szűrést az spp_boktmpext1 végzi.
  // Üres Ig kitöltött Tól mellett a backendben Tól+'Z'-re egészül ki (prefix-tartomány).
  specDim1From: string; // Munkaszám
  specDim1To: string;
  specDim2From: string; // Gépjármű
  specDim2To: string;
  specDim3From: string; // Szerelő
  specDim3To: string;
  specDim4From: string; // Szerviztípus
  specDim4To: string;

  // ===== Választó-fülek kijelölt elemei (K2D ilboktmpext* listák) =====
  // Üres lista = az a fül nem szűr; nem üres = IN-szűrő a from/to tartományra rá-ÉS-elve.
  accnoList: string[]; // Fksz fül – accno kódok
  dimcode1List: string[]; // Dim 1-3 fül – dimcode kódok type szerint bontva (2/3/4)
  dimcode2List: string[];
  dimcode3List: string[];
  // Dim 4-6 / 7-9 / 10-12 fülek (csak is_dim_4_9='+' mellett): dim N = type N+1 (5..13).
  dimcode4List: string[];
  dimcode5List: string[];
  dimcode6List: string[];
  dimcode7List: string[];
  dimcode8List: string[];
  dimcode9List: string[];
  dimcode10List: string[];
  dimcode11List: string[];
  dimcode12List: string[];
  // VAGY-kapcsolat a fül három dim-listája közt (as_dim_or4_6/7_9/10_12); a fülön állítható,
  // K2D-ben a cbx_dim_or* alapból bekapcsolt (srw:5927-5928) és kijelölés mellett látszik.
  dimOr46: boolean;
  dimOr79: boolean;
  dimOr1012: boolean;
  partnerIds: number[]; // Partner fül – partner ID-k (szam)
  carIds: number[]; // Gépjármű fül – sercar ID-k (szam)
  projectIds: number[]; // Project fül – hmgenproject ID-k (szam)
  poIds: number[]; // Megr. fül – hmgenpurchorder ID-k (szam)
}

export const DefaultGeneralLedgerStatementFilter: GeneralLedgerStatementFilter = {
  reportType: StatementReportType.Brief,

  yearId: 0,
  fromDate: null,
  toDate: null,
  fromAccno: "0",
  toAccno: "9999999999",
  listLevel: 1,

  isOpenOnly: false,
  isOpenTotal: true,
  // K2D: a gs_bok_is_open_total='+' rendszerparam a from_first-öt is '+'-ra állítja (srw 4382-4384).
  fromFirst: true,
  isOpen: false,
  isClosed: false,
  isCloseOnly: false,
  isPrevClose: false,
  isForeignCurrency: false,

  isManual: true,
  isMachine: true,
  isStorno: false,
  isAcross: false,

  // K2D: a kivonat-open expliciten '-'-ra állítja (srw 2250); a képernyőn alapból kikapcsolt.
  isContraAcc: false,
  isPartner0: true,
  noPartner: false,
  isPartner: false,
  isCar: false,
  partnerCarNewPage: false,
  ejectAfter: false,
  isLevelStyleDiff: false,
  invoiceGroup: false,
  isEnlNote: false,
  isMix: true,

  isDimcode1: false,
  fromDimcode1: "0",
  toDimcode1: "ZZZZZZZZZZ",
  isDimcode2: false,
  fromDimcode2: "0",
  toDimcode2: "ZZZZZZZZZZ",
  isDimcode3: false,
  fromDimcode3: "0",
  toDimcode3: "ZZZZZZZZZZ",
  // K2D: a Dim 1-3 fül rb_dim_or radiója alapból bekapcsolt (srw:5616-5617) → VAGY.
  isDimOr: true,

  getGeneralLedger: true,
  getAccumulator: false,
  getAccountClass: false,
  // K2D: a transid (Tr.azon. látható) initial értéke '-' – alapból nem látszik.
  showTransId: false,
  ejectAfterAccno: false,
  connectedAccno: true, // K2D karton: alapból bepipálva
  isGroupDim1: false,
  isGroupDim2: false,
  isGroupDim3: false,
  // K2D rendez értékkészlet (d_f_hlboktmpexp_brief.srd): Dátum=1 / Összeg=2 / Partner=3 / Számlaszám=4.
  sortBy: "1",
  // K2D pmode initial "1" = a csgenpaymode "-" placeholder sora (az SP-ben 1 = mind).
  payMode: 1,
  reconInclude: "all",

  specDim1From: "",
  specDim1To: "",
  specDim2From: "",
  specDim2To: "",
  specDim3From: "",
  specDim3To: "",
  specDim4From: "",
  specDim4To: "",

  accnoList: [],
  dimcode1List: [],
  dimcode2List: [],
  dimcode3List: [],
  dimcode4List: [],
  dimcode5List: [],
  dimcode6List: [],
  dimcode7List: [],
  dimcode8List: [],
  dimcode9List: [],
  dimcode10List: [],
  dimcode11List: [],
  dimcode12List: [],
  // K2D: a cbx_dim_or* checkboxok initial értéke checked (srw:5927-5928) → VAGY.
  dimOr46: true,
  dimOr79: true,
  dimOr1012: true,
  partnerIds: [],
  carIds: [],
  projectIds: [],
  poIds: [],
};

// Friss filter-példány: a lista-mezők ÚJ tömböket kapnak – a sima {...Default} spread a tömb-
// referenciákat osztaná meg, és a defaultba szivárogna a kijelölés.
export const NewGeneralLedgerStatementFilter = (): GeneralLedgerStatementFilter => ({
  ...DefaultGeneralLedgerStatementFilter,
  accnoList: [],
  dimcode1List: [],
  dimcode2List: [],
  dimcode3List: [],
  dimcode4List: [],
  dimcode5List: [],
  dimcode6List: [],
  dimcode7List: [],
  dimcode8List: [],
  dimcode9List: [],
  dimcode10List: [],
  dimcode11List: [],
  dimcode12List: [],
  partnerIds: [],
  carIds: [],
  projectIds: [],
  poIds: [],
});
